// Store module para WhatsApp CRM
import axios from 'axios'

const state = {
  conversations: [],
  currentConversation: null,
  currentMessages: [],
  notifications: [],
  connectionStatus: 'disconnected', // 'disconnected', 'connecting', 'connected'
  lastUpdate: null
}

const mutations = {
  SET_CONVERSATIONS(state, conversations) {
    state.conversations = conversations
    state.lastUpdate = Date.now()
  },
  
  UPDATE_CONVERSATION(state, conversation) {
    const index = state.conversations.findIndex(c => c.id === conversation.id)
    if (index !== -1) {
      // Actualizar conversación existente
      state.conversations.splice(index, 1, conversation)
    } else {
      // Agregar nueva conversación
      state.conversations.unshift(conversation)
    }
    
    // Si es la conversación actual, también actualizarla
    if (state.currentConversation && state.currentConversation.id === conversation.id) {
      state.currentConversation = conversation
    }
    
    state.lastUpdate = Date.now()
  },
  
  SET_CURRENT_CONVERSATION(state, conversation) {
    state.currentConversation = conversation
  },
  
  SET_CURRENT_MESSAGES(state, messages) {
    state.currentMessages = messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
  },
  
  ADD_MESSAGE(state, message) {
    if (!message || !message.id) {
      console.error('Intentando añadir un mensaje inválido:', message)
      return
    }
    
    // Verificar que no esté duplicado
    const exists = state.currentMessages.find(m => m.id === message.id)
    if (!exists) {
      state.currentMessages.push(message)
      // Mantener orden cronológico
      state.currentMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    }
    
    // Actualizar última actividad de la conversación si es la actual
    if (state.currentConversation && message.conversation && state.currentConversation.id === message.conversation) {
      state.currentConversation.last_message_at = message.timestamp
      
      // Actualizar también en la lista de conversaciones
      const convIndex = state.conversations.findIndex(c => c.id === message.conversation)
      if (convIndex !== -1) {
        state.conversations[convIndex].last_message_at = message.timestamp
      }
    }
    
    state.lastUpdate = Date.now()
  },
  
  ADD_NOTIFICATION(state, notification) {
    state.notifications.unshift({
      id: Date.now(),
      ...notification,
      timestamp: Date.now(),
      read: false
    })
    
    // Mantener solo las últimas 20 notificaciones
    if (state.notifications.length > 20) {
      state.notifications = state.notifications.slice(0, 20)
    }
  },
  
  MARK_NOTIFICATION_READ(state, notificationId) {
    const notification = state.notifications.find(n => n.id === notificationId)
    if (notification) {
      notification.read = true
    }
  },
  
  CLEAR_NOTIFICATIONS(state) {
    state.notifications = []
  },
  
  SET_CONNECTION_STATUS(state, status) {
    state.connectionStatus = status
  }
}

const actions = {
  async loadConversations({ commit }, filters = {}) {
    try {
      const response = await axios.get('/communications/api/conversations/', { params: filters })
      commit('SET_CONVERSATIONS', response.data)
      return response.data
    } catch (error) {
      console.error('Error loading conversations:', error)
      throw error
    }
  },
  
  async loadConversationDetails({ commit }, conversationId) {
    try {
      const response = await axios.get(`/communications/api/conversations/${conversationId}/`)
      const conversation = response.data.conversation || response.data
      const messages = response.data.messages || []
      
      commit('SET_CURRENT_CONVERSATION', conversation)
      commit('SET_CURRENT_MESSAGES', messages)
      
      return { conversation, messages }
    } catch (error) {
      console.error('Error loading conversation details:', error)
      throw error
    }
  },
  
  // Handlers para eventos WebSocket
  handleNewMessage({ commit, state }, data) {
    const { message, conversation_id } = data
    
    // Agregar mensaje si es de la conversación actual
    if (state.currentConversation && state.currentConversation.id === conversation_id) {
      commit('ADD_MESSAGE', message)
    }
    
    // Crear notificación
    const notificationText = message.sender_type === 'contact' 
      ? `Nuevo mensaje de cliente en conversación ${conversation_id}`
      : `Nuevo mensaje de ${message.sender_type} en conversación ${conversation_id}`
    
    commit('ADD_NOTIFICATION', {
      type: 'new_message',
      title: 'Nuevo Mensaje',
      message: notificationText,
      conversationId: conversation_id,
      messageId: message.id
    })
    
    // Actualizar conversación en la lista
    const conversation = state.conversations.find(c => c.id === parseInt(conversation_id))
    if (conversation) {
      conversation.last_message_at = message.timestamp
      // Mover al inicio de la lista
      const index = state.conversations.indexOf(conversation)
      if (index > 0) {
        state.conversations.splice(index, 1)
        state.conversations.unshift(conversation)
      }
    }
  },
  
  handleConversationUpdate({ commit }, data) {
    const { conversation_data } = data
    commit('UPDATE_CONVERSATION', conversation_data)
    
    commit('ADD_NOTIFICATION', {
      type: 'conversation_updated',
      title: 'Conversación Actualizada',
      message: `Conversación ${conversation_data.id} fue actualizada`,
      conversationId: conversation_data.id
    })
  },
  
  handleAgentTakeover({ commit }, data) {
    const { conversation_id, agent_data } = data
    
    commit('ADD_NOTIFICATION', {
      type: 'agent_takeover',
      title: 'Agente Tomó Control',
      message: `${agent_data.first_name || agent_data.email} tomó control de la conversación ${conversation_id}`,
      conversationId: conversation_id,
      agentId: agent_data.id
    })
  },
  
  handleBotReset({ commit }, data) {
    const { conversation_id } = data
    
    commit('ADD_NOTIFICATION', {
      type: 'bot_reset',
      title: 'Conversación Devuelta al Bot',
      message: `Conversación ${conversation_id} fue devuelta al bot`,
      conversationId: conversation_id
    })
  },
  
  setConnectionStatus({ commit }, status) {
    commit('SET_CONNECTION_STATUS', status)
  }
}

const getters = {
  conversations: state => state.conversations || [],
  unreadNotifications: state => state.notifications.filter(n => !n.read),
  unreadNotificationCount: (state, getters) => getters.unreadNotifications.length,
  isConnected: state => state.connectionStatus === 'connected',
  currentConversationMessages: state => state.currentMessages || [],
  conversationById: state => id => state.conversations.find(c => c.id === id)
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
